<script setup lang="ts">
const formatter = createCurrencyFormatter();

import { undefinedIfNullOrWhiteSpace } from "../../../../shared/utils/string";

// eslint-disable-next-line no-undef
const props = defineProps({
    product: {
        type: Object as PropType<AuctionProduct>,
        required: true,
    },
    setup: {
        type: Object as PropType<Setup>,
        required: true,
    },
    overwriteSettings: Object as PropType<OverwriteSettings>,
    phoneValidation: Object as PropType<PhoneValidation>,
});

import labels from "../../shared/labels";
import { computed, type PropType } from "vue";
import { type Setup, type AuctionProduct, type PhoneValidation } from "./types";
import CountdownComponent from "./countdown.vue";
import BidForm from "./bid-form.vue";
import BuyNowForm from "./buy-now-form.vue";
import { createCurrencyFormatter } from "../common/amount-format";
import FadeAndSlide from "../../shared/FadeAndSlide.vue";
import { isValidSecondsleft } from "../../../commons/product/auction/countdown";
import { type OverwriteSettings } from "../../../commons/product/overwrite-settings";

const openForBid = computed(
    () =>
        props.product.hasAuction &&
        props.product.openForBid &&
        props.product.timeIsNotStarted === false &&
        props.product.timeTotalSeconds > 0
);

const showBidForm = computed(
    () => props.product.hasAuction && openForBid.value
);
const showBuyNowForm = computed(
    () => props.product.hasBuyNow || props.product.hasLoserOffer
);

function withTrailing(value: string | null | undefined, trail: string) {
    if (value == null) return value;
    return value + trail;
}

function formatAmounts(value: number) {
    return formatter.format(value);
}
</script>

<template>
    <div :class="$style['specs-component']">
        <div
            :class="$style['countdown-container']"
            v-if="isValidSecondsleft(props.product.timeTotalSeconds)"
        >
            <countdown-component
                :class="$style['countdown-component']"
                :time-is-not-started="product.timeIsNotStarted"
                :seconds-left="product.timeTotalSeconds"
            ></countdown-component>
        </div>
        <div
            :class="[
                $style['bid-container'],
                product.winningBid?.currentUser == true
                    ? $style['current-user-highest-bid']
                    : '',
                !showBuyNowForm && !showBidForm ? $style['nothing-below'] : '',
            ]"
            v-if="product.hasAuction"
        >
            <div :class="$style['total-bidders']" v-if="product.bidders > 2">
                {{ labels.frontendProductpage.auction.totalBidders }}:
                <strong>{{ product.bidders }}</strong>
            </div>
            <div
                :class="$style['highest-bidder']"
                v-if="product.winningBid != null"
            >
                {{
                    labels.frontendProductpage.auction[
                        product.winningBid.currentUser
                            ? "highestBidderCurrentUser"
                            : "highestBidder"
                    ]
                }}
                <strong>{{ product.winningBid.name }}</strong>
            </div>
            <div :class="$style['amounts']">
                <div
                    :class="$style['bid-amount']"
                    v-if="product.currentBid != null"
                >
                    <span :class="$style['bid-amount-header']"
                        >{{
                            product.winningBid == null
                                ? labels.frontendProductpage.auction
                                      .minimumPrice
                                : labels.frontendProductpage.auction.currentBid
                        }}:&nbsp;</span
                    >
                    <span :class="$style['bid-amount-value']">{{
                        formatAmounts(product.currentBid)
                    }}</span>
                </div>
                <div
                    :class="$style['estimated-price']"
                    v-if="
                        product.showEstimatedPrice &&
                        product.estimatedPrice != null
                    "
                >
                    {{
                        withTrailing(
                            undefinedIfNullOrWhiteSpace(
                                overwriteSettings?.overwriteEstimateLabel
                            ),
                            ":"
                        ) ??
                        labels.frontendProductpage.specs
                            .shopEstimatedProductPrice
                    }}
                    {{ formatAmounts(product.estimatedPrice) }}
                </div>
            </div>
        </div>
        <fade-and-slide>
            <div :class="$style['bid-form-container']" v-if="showBidForm">
                <BidForm
                    :product="product"
                    :setup="setup"
                    :phone-validation="phoneValidation"
                ></BidForm>
            </div>
        </fade-and-slide>
        <div :class="$style['hr']" v-if="showBidForm && showBuyNowForm"></div>
        <div :class="$style['buy-now-container']" v-if="showBuyNowForm">
            <BuyNowForm :product="product"></BuyNowForm>
        </div>
    </div>
</template>

<style module lang="scss">
@import "../../../../responsive/style.scss";

$padding: 12px;

.hr {
    margin-top: $padding;
    height: 1px;
    width: 100%;
    background: #272727;
}

.icon-stock-status {
    width: 12px;
    height: 12px;
    display: inline-block;
    border-radius: 50%;
    margin-right: 5px;
    vertical-align: middle;
}

.viabill-section {
    margin-top: 10px;
    line-height: 12px;
    font-size: 10px;
    color: #666666;
    max-height: 25px;
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 50%;

    @include media-breakpoint-up(lg) {
        order: 1;
    }
}

.add-to-cart-comp {
    flex-basis: 100%;

    @include media-breakpoint-up(lg) {
        flex-basis: auto;
    }
}

.specs-component {
    color: #272727;
    border: 1px solid #d8d8d8;
    border-radius: 4px;
    overflow: hidden;
    padding: $padding;
    background-color: rgb(246, 242, 230);

    .countdown-container {
        margin-top: -$padding;
        margin-left: -$padding;
        margin-right: -$padding;

        .countdown-component {
            padding: $padding;
        }
    }

    .bid-container {
        display: flex;
        flex-direction: column;
        background-color: #e5dec7;
        align-items: start;
        padding: 10px $padding;
        margin-left: -$padding;
        margin-right: -$padding;
        transition: background-color 400ms ease-out,
            margin-bottom 400ms ease-out;
        margin-bottom: 0;
        &.nothing-below {
            margin-bottom: -$padding;
        }

        &.current-user-highest-bid {
            background-color: #ddeac3;
        }

        @include media-breakpoint-up(lg) {
            display: block;
            flex-direction: unset;
            align-items: unset;
            margin-top: 0;
        }

        .highest-bidder {
            font-size: 12px;
        }
        .total-bidders {
            font-size: 12px;
        }

        .amounts {
            display: flex;
            flex-direction: row;
            align-items: center;

            .bid-amount {
                font-size: 30px;

                .bid-amount-header {
                    font-weight: 700;
                }
                .bid-amount-value {
                    font-weight: 800;
                }
            }
            .estimated-price {
                font-family: "Open Sans";
                font-size: 14px;
                text-decoration: underline;

                @include media-breakpoint-up(lg) {
                    margin-top: 0;
                    .bid-amount + & {
                        margin-left: 10px;
                    }
                }
            }

            .bid-amount + .estimated-price {
                margin-left: 20px;
            }
        }
    }

    .bid-form-container {
        padding: 0;
        padding-top: $padding;
    }

    .buy-now-container {
        padding: 0;
        padding-top: $padding;

        &:first-child {
            padding-top: 0;
        }
    }
}
</style>
